import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NewPersonForm } from "./NewPersonForm";
import { PersonPicker } from "./PersonPicker";
import {
  IcCalendar,
  IcCheck,
  IcClock,
  IcPeople,
  IcUserPlus,
  IcWallet,
} from "./icons";

/**
 * The quick-add sheet behind the centre button.
 *
 * The actions are the ones the spec names as daily field work: capture a
 * contact, log an interaction, mark group attendance (§4.6 — must take under
 * 30 seconds), record a memorial (§4.7), open a task. Everything here is a
 * capture action; nothing here is administration, which belongs on desktop.
 *
 * Items that are not built yet are shown disabled rather than hidden, so the
 * intended shape of the app is visible and no button lies about working.
 *
 * ## The half of that rule nobody was enforcing
 *
 * Showing an unbuilt action greyed out is honest. Leaving it greyed out **after
 * it has been built** is the same lie in reverse, and it is worse, because the
 * app is now the thing telling you the feature does not exist.
 *
 * That is exactly what happened to manual donations. `recordManualDonation` was
 * written, tested, deployed to both environments and wired into the person card
 * — and the product owner said "זה עוד לא באוויר", which from where he was
 * standing was true: this sheet is the first place anybody looks for "add
 * something", and it said, in Hebrew, underneath a greyed-out row, that manual
 * donations had not been built yet.
 *
 * **Anything reachable from here must be reachable in one move.** Recording cash
 * starts from "somebody just handed me two hundred shekels", not from browsing
 * to their card and scrolling past five sections — so the person is picked here
 * and the sheet opens on their card with the form already up.
 */
interface QuickAddItem {
  id: string;
  label: string;
  hint: string;
  icon: React.ReactNode;
  tint: { bg: string; fg: string };
  ready?: boolean;
  /** Where this action actually lives, when it needs context a sheet cannot
   *  supply — a person to log against, an event to mark attendance at. */
  goTo?: string;
}

const ITEMS: QuickAddItem[] = [
  {
    id: "person",
    label: "איש קשר חדש",
    hint: "שם וטלפון. את השאר אפשר אחר כך",
    icon: <IcUserPlus />,
    tint: { bg: "var(--orange-soft)", fg: "var(--orange-2)" },
    ready: true,
  },
  {
    id: "interaction",
    label: "תיעוד קשר",
    hint: "שיחה, וואטסאפ או פגישה שהיו",
    icon: <IcClock />,
    tint: { bg: "var(--slate-soft)", fg: "var(--slate)" },
    // Picks the person here and opens the form on their card. It used to drop
    // you on the People list with a sentence telling you what to do next, which
    // is an instruction where a button belongs.
    ready: true,
  },
  {
    id: "attendance",
    label: "סימון נוכחות",
    hint: "מי היה בתפילה, בשיעור או בשבת",
    icon: <IcPeople />,
    tint: { bg: "var(--green-soft)", fg: "var(--green)" },
    goTo: "/rollcall",
    ready: true,
  },
  {
    id: "memorial",
    label: "יארצייט / אזכרה",
    hint: "תאריך עברי וקשר לנפטר",
    icon: <IcCalendar />,
    tint: { bg: "var(--amber-soft)", fg: "var(--amber)" },
  },
  {
    id: "donation",
    label: "תרומה שהתקבלה ידנית",
    hint: "מזומן, העברה או צ׳ק שלא עברו בסליקה",
    icon: <IcWallet />,
    tint: { bg: "var(--amber-soft)", fg: "var(--amber)" },
    ready: true,
  },
  {
    id: "task",
    label: "משימה",
    hint: "משהו לחזור אליו, עם תאריך",
    icon: <IcCheck />,
    tint: { bg: "var(--slate-soft)", fg: "var(--slate)" },
    goTo: "/tasks?new=1",
    ready: true,
  },
];

export function QuickAdd({
  onClose,
  onOpenPerson,
}: {
  onClose: () => void;
  onOpenPerson: (personId: string) => void;
}) {
  const [openForm, setOpenForm] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const TITLES: Record<string, string> = {
    person: "איש קשר חדש",
    donation: "תרומה שהתקבלה ידנית",
    interaction: "תיעוד קשר",
  };
  const title = (openForm && TITLES[openForm]) ?? "הוספה מהירה";

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="sheet" role="dialog" aria-label={title} aria-modal="true">
        <div className="sheet-grip" />

        <div className="sheet-head">
          {openForm && (
            // A back arrow, not a close: cancelling one form should return to
            // the list rather than throwing the user out of the sheet entirely.
            <button
              type="button"
              className="sheet-back"
              onClick={() => setOpenForm(null)}
              aria-label="חזרה"
            >
              ›
            </button>
          )}
          <h2>{title}</h2>
        </div>

        {openForm === "person" ? (
          <NewPersonForm onClose={onClose} onOpenPerson={onOpenPerson} />
        ) : openForm === "donation" || openForm === "interaction" ? (
          /*
           * Both of these need a person before they can ask anything else, and
           * both already have a finished form on the person's card. So this
           * picks the person and hands over, rather than building a second copy
           * of a sheet that exists — two copies of a money form is how the two
           * start disagreeing about what an amount means.
           */
          <div style={{ padding: "0 14px 18px" }}>
            <p className="faint" style={{ marginTop: 0 }}>
              {openForm === "donation"
                ? "של מי התרומה? חפשו לפי שם או טלפון."
                : "עם מי דיברתם?"}
            </p>
            <PersonPicker
              personId={null}
              personName=""
              placeholder="חיפוש לפי שם או טלפון…"
              onPick={(person) => {
                if (!person) return;
                onClose();
                navigate(
                  `/people/${person.id}?${openForm === "donation" ? "donate=1" : "log=1"}`,
                );
              }}
            />
          </div>
        ) : (
          <>
            {ITEMS.map((it) => (
              <button
                key={it.id}
                className="sheet-item"
                disabled={!it.ready}
                onClick={
                  !it.ready
                    ? undefined
                    : it.goTo
                      ? () => {
                          onClose();
                          navigate(it.goTo!);
                        }
                      : () => setOpenForm(it.id)
                }
              >
                <span className="rail-ico" style={{ background: it.tint.bg, color: it.tint.fg }}>
                  {it.icon}
                </span>
                <span className="grow">
                  {it.label}
                  <span className="faint" style={{ display: "block", marginTop: 2 }}>
                    {it.hint}
                  </span>
                </span>
              </button>
            ))}

            {/* Names only what is genuinely missing. The previous version of
                this line claimed manual donations and tasks were unbuilt months
                after both shipped. */}
            <p className="faint" style={{ textAlign: "center", padding: "10px 14px 0" }}>
              יארצייט ואזכרה עוד לא מוכנים. הם מופיעים כאן כדי שתדעו שהם בדרך.
            </p>
          </>
        )}
      </div>
    </>
  );
}
